/**
 * Accurate Twitter/X post character limit counting and validation utility.
 *
 * References:
 * - Twitter/X standard post limit is 280 characters.
 * - URLs are transformed by Twitter to t.co links and count as 23 characters regardless of length.
 * - Emojis and CJK / fullwidth characters count as 2 weighted characters.
 * - Basic Latin / ASCII characters count as 1 character.
 */

export const X_POST_MAX_LIMIT = 280;
export const X_URL_WEIGHTED_LENGTH = 23;

const URL_REGEX = /https?:\/\/[^\s]+/gi;

/**
 * Check if a character or code point has weight 2 under Twitter's weighted character counting rules.
 */
function isWeight2(codePoint: number): boolean {
  return (
    // Emojis, supplementary symbols, astral code points
    codePoint > 0xffff ||
    // CJK Radicals, Kangxi, Ideographic Description, CJK Symbols & Punctuation
    (codePoint >= 0x2e80 && codePoint <= 0x2fff) ||
    (codePoint >= 0x3000 && codePoint <= 0x9fff) ||
    // Hangul Syllables
    (codePoint >= 0xac00 && codePoint <= 0xd7af) ||
    // CJK Compatibility
    (codePoint >= 0xf900 && codePoint <= 0xfaff) ||
    // Fullwidth forms
    (codePoint >= 0xff01 && codePoint <= 0xff60) ||
    (codePoint >= 0xffe0 && codePoint <= 0xffe6)
  );
}

export interface XValidationResult {
  weightedLength: number;
  maxLimit: number;
  remaining: number;
  percentUsed: number;
  isEmpty: boolean;
  isOverLimit: boolean;
  isValid: boolean;
  urlCount: number;
}

/**
 * Calculates weighted character length according to verified X/Twitter rules.
 */
export function calculateXPostLength(text: string): XValidationResult {
  if (!text) {
    return {
      weightedLength: 0,
      maxLimit: X_POST_MAX_LIMIT,
      remaining: X_POST_MAX_LIMIT,
      percentUsed: 0,
      isEmpty: true,
      isOverLimit: false,
      isValid: false,
      urlCount: 0,
    };
  }

  // 1. Extract URLs and replace them with fixed 23-char placeholders
  const matches = text.match(URL_REGEX) || [];
  const urlCount = matches.length;
  const textWithoutUrls = text.replace(URL_REGEX, "");

  // 2. Count weight of non-URL characters
  let weight = urlCount * X_URL_WEIGHTED_LENGTH;

  // Use Array.from to correctly handle unicode surrogate pairs / multi-byte emojis
  const codePoints = Array.from(textWithoutUrls);
  for (const char of codePoints) {
    const cp = char.codePointAt(0) || 0;
    weight += isWeight2(cp) ? 2 : 1;
  }

  const isEmpty = text.trim().length === 0;
  const isOverLimit = weight > X_POST_MAX_LIMIT;
  const isValid = !isEmpty && !isOverLimit;
  const remaining = X_POST_MAX_LIMIT - weight;
  const percentUsed = Math.min(100, Math.round((weight / X_POST_MAX_LIMIT) * 100));

  return {
    weightedLength: weight,
    maxLimit: X_POST_MAX_LIMIT,
    remaining,
    percentUsed,
    isEmpty,
    isOverLimit,
    isValid,
    urlCount,
  };
}
